import { Add, Remove } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Skeleton, Typography } from '@mui/material';
import { FieldArray, Formik } from 'formik';
import { isEmpty, pick } from 'ramda';
import { Fragment, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { DateTime, parseDate } from '@jebel/utils';

import { useUserCommunityMembership } from 'features/community/hooks';
import { CheckboxField } from 'shared/components/form';
import {
  AutocompleteField,
  Button,
  FormKeyboardDatePicker,
  FormPhoneField,
  FormSelect,
  FormTextField,
  LoadingOverlay,
} from 'shared/components/ui';
import { FIELDS_CONFIG_USER, US_STATES_OPTIONS } from 'shared/constants';
import { UsersUserPreferencesUpdateRelationInput, UserUpdateInput } from 'shared/graphql';
import { useCrudPermissions, useToast, useUserById, useUserUpdate } from 'shared/hooks';
import { required } from 'shared/utils/form';
import { recordError } from 'shared/utils/record';

import { Buttons, Container, Content, Form, FormSection, Header } from './MemberDetailsPage.styles';

type Parent = {
  fullName: string;
  classYear?: string | null;
};

type FormData = {
  firstName?: string;
  lastName?: string;
  gender?: string;
  affiliation?: string;
  affiliationDescription?: string;
  graduatingYear?: string;
  birthDate?: DateTime | null;
  parents?: Array<Parent>;
  description?: string;
  isParentsAvailable?: boolean;
  userPreferences?: {
    phoneNumber?: string;
    secondaryPhoneNumber?: string;
    address?: {
      zip?: string | null;
      city?: string | null;
      state?: string | null;
    };
  };
};

const UPDATING_MESSAGE_KEY = 'UPDATING_MESSAGE_KEY';

export function MemberDetailsPage() {
  const {
    membersPermissions: { edit: isEditAccessed },
    loading: loadingAddons,
  } = useCrudPermissions();

  const { id: userId } = useParams<{ id: string }>();
  const { user, loading } = useUserById({ id: userId });
  const { onUserUpdate } = useUserUpdate({ id: userId });
  const { goBack } = useHistory();
  const { data: membership } = useUserCommunityMembership(userId);
  const { showError, showMessage, dismiss } = useToast();

  const initials = useMemo<FormData>(() => {
    if (!user) {
      return {};
    }

    const picked = pick(
      [
        'firstName',
        'lastName',
        'gender',
        'affiliation',
        'graduatingYear',
        'birthDate',
        'parents',
        'description',
        'isParentsAvailable',
        'userPreferences',
      ],
      user as NonNullable<typeof user>,
    );

    const initialParents = [
      {
        fullName: '',
        classYear: '',
      },
    ];

    const birthDate = parseDate(user?.birthDate);

    const payload: FormData = {
      firstName: picked?.firstName ?? undefined,
      lastName: picked?.lastName ?? undefined,
      gender: picked?.gender ?? undefined,
      affiliation: picked?.affiliation ?? undefined,
      graduatingYear: picked?.graduatingYear ?? undefined,
      birthDate: null,

      parents: isEmpty(picked?.parents || []) ? initialParents : picked?.parents,
      isParentsAvailable: !isEmpty(picked?.parents),

      userPreferences: {
        phoneNumber: picked?.userPreferences?.phoneNumber ?? '',
        secondaryPhoneNumber: picked?.userPreferences?.secondaryPhoneNumber ?? '',
        address: {
          zip: picked?.userPreferences?.address?.zip ?? '',
          city: picked?.userPreferences?.address?.city ?? '',
          state: picked?.userPreferences?.address?.state ?? '',
        },
      },
    };

    if (birthDate.isValid) {
      payload.birthDate = birthDate;
    }

    return payload;
  }, [user]);

  const handleSubmit = async (form: FormData) => {
    try {
      const birthDate = form.birthDate?.endOf('day');
      const parents = form.isParentsAvailable ? form.parents : [];

      const userPreferences: UsersUserPreferencesUpdateRelationInput = {
        [user?.userPreferences?.id ? 'update' : 'create']: {
          phoneNumber: form.userPreferences?.phoneNumber,
          secondaryPhoneNumber: form.userPreferences?.secondaryPhoneNumber,

          address: {
            [user?.userPreferences?.address?.id ? 'update' : 'create']:
              form.userPreferences?.address,
          },
        },
      };

      const data: UserUpdateInput = {
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender,
        graduatingYear: form.graduatingYear,

        // Save for compatibility with the old affiliation field.
        // https://github.com/jebelapp/jebel/issues/1841
        affiliation: form.affiliation,
        affiliationDescription: form.affiliationDescription,

        birthDate: null,
        userPreferences,
        parents,
      };

      if (membership) {
        data.schoolMemberships = {
          update: [
            {
              filter: { id: membership.id },
              data: {
                // Save in the new affiliation field.
                // https://github.com/jebelapp/jebel/issues/1841
                affiliation: form.affiliation,
                affiliationDescription: form.affiliationDescription,
              },
            },
          ],
        };
      }

      if (birthDate?.isValid) {
        data.birthDate = birthDate.toSQLDate();
      }

      showMessage('Updating your account information.', { id: UPDATING_MESSAGE_KEY });

      await onUserUpdate(data);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(UPDATING_MESSAGE_KEY);
    }
  };

  return (
    <Container>
      <Header>
        <Typography variant="h6">Account Information</Typography>
      </Header>

      <Content>
        <LoadingOverlay
          loading={loading}
          overlay={
            <Form>
              <FormSection>
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
              </FormSection>

              <FormSection>
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
                <Skeleton variant="rectangular" height={54} />
              </FormSection>
            </Form>
          }
        >
          <Formik enableReinitialize initialValues={initials} onSubmit={handleSubmit}>
            {({ isSubmitting, resetForm, values, ...form }) => (
              <Form>
                <FormSection>
                  <FormTextField
                    disabled={!isEditAccessed}
                    inputProps={{
                      label: 'First Name',
                      variant: 'outlined',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'firstName', validate: required }}
                  />

                  <FormTextField
                    disabled={!isEditAccessed}
                    inputProps={{
                      label: 'Last Name',
                      variant: 'outlined',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'lastName', validate: required }}
                  />

                  <FormPhoneField
                    inputProps={{
                      label: 'Primary Phone Number',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'userPreferences.phoneNumber' }}
                  />

                  <FormPhoneField
                    inputProps={{
                      label: 'Secondary Phone Number',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{
                      name: 'userPreferences.secondaryPhoneNumber',
                      validate: undefined,
                    }}
                  />
                </FormSection>
                <FormSection>
                  <FormSelect
                    selectProps={{
                      children: FIELDS_CONFIG_USER.gender.map(item => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      )),
                      variant: 'outlined',
                      label: 'Gender',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'gender' }}
                  />
                  <FormSelect
                    selectProps={{
                      children: FIELDS_CONFIG_USER.affiliation.map(item => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      )),
                      variant: 'outlined',
                      label: 'Affiliation',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'affiliation', validate: required }}
                  />

                  {values.affiliation === 'alumni' && (
                    <FormSelect
                      selectProps={{
                        children: FIELDS_CONFIG_USER.graduatingYear.map(item => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        )),
                        variant: 'outlined',
                        label: 'Graduation Year',
                        disabled: !isEditAccessed,
                      }}
                      fieldProps={{ name: 'graduatingYear', validate: required }}
                    />
                  )}

                  {values.affiliation === 'other' && (
                    <FormTextField
                      inputProps={{
                        label: 'Affiliation Description',
                        variant: 'outlined',
                        disabled: !isEditAccessed,
                      }}
                      fieldProps={{ name: 'affiliationDescription', validate: required }}
                    />
                  )}
                </FormSection>

                <FormSection>
                  <CheckboxField
                    name="isParentsAvailable"
                    label="Parent (current student or alumni)?"
                  />
                </FormSection>

                {values.isParentsAvailable && (
                  <FieldArray
                    name="parents"
                    render={arrayHelpers => {
                      return (
                        <Fragment>
                          {values.parents?.map((parent, index) => (
                            <FormSection key={index}>
                              <FormTextField
                                disabled={!isEditAccessed}
                                fieldProps={{
                                  name: `parents.${index}.fullName`,
                                }}
                                inputProps={{
                                  label: 'Full Name',
                                  variant: 'outlined',
                                }}
                              />
                              <Box
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <FormSelect
                                  selectProps={{
                                    children: FIELDS_CONFIG_USER.graduatingYear.map(item => (
                                      <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                      </MenuItem>
                                    )),
                                    variant: 'outlined',
                                    label: 'Class Year',
                                    disabled: !isEditAccessed,
                                  }}
                                  fieldProps={{
                                    name: `parents.${index}.classYear`,
                                  }}
                                />
                                <IconButton
                                  onClick={() => arrayHelpers.remove(index)}
                                  size="small"
                                  color="error"
                                  style={{
                                    height: 'min-content',
                                  }}
                                >
                                  <Remove />
                                </IconButton>
                              </Box>
                            </FormSection>
                          ))}
                          <Button
                            variant="text"
                            disabled={!isEditAccessed}
                            color="secondary"
                            onClick={() => arrayHelpers.push('')}
                            startIcon={<Add />}
                            style={{
                              width: '130px',
                              textTransform: 'none',
                            }}
                          >
                            Add another
                          </Button>
                        </Fragment>
                      );
                    }}
                  />
                )}

                <FormSection>
                  <FormKeyboardDatePicker
                    datePickerProps={{
                      allowKeyboardControl: true,
                      disableFuture: true,
                      inputVariant: 'outlined',
                      label: 'Date of birth',
                      variant: 'inline',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'birthDate' }}
                  />
                  <FormTextField
                    disabled={!isEditAccessed}
                    inputProps={{
                      label: 'ZIP Code',
                      variant: 'outlined',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'userPreferences.address.zip' }}
                  />
                  <FormTextField
                    disabled={!isEditAccessed}
                    inputProps={{
                      label: 'City',
                      variant: 'outlined',
                      disabled: !isEditAccessed,
                    }}
                    fieldProps={{ name: 'userPreferences.address.city' }}
                  />

                  <AutocompleteField
                    label="State"
                    name="userPreferences.address.state"
                    options={US_STATES_OPTIONS}
                    disabled={!isEditAccessed}
                  />
                </FormSection>

                <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                  <Button
                    disabled={isSubmitting}
                    size="medium"
                    variant="outlined"
                    onClick={() => goBack()}
                  >
                    Go Back
                  </Button>

                  <Buttons>
                    <Button
                      disabled={isSubmitting || !form.dirty}
                      size="medium"
                      variant="outlined"
                      type="reset"
                      onClick={() => resetForm()}
                    >
                      Cancel
                    </Button>

                    <Button
                      disabled={isSubmitting || !form.dirty || !isEditAccessed || loadingAddons}
                      color="primary"
                      disableElevation
                      variant="contained"
                      type="submit"
                      loading={isSubmitting}
                    >
                      Save changes
                    </Button>
                  </Buttons>
                </Box>
              </Form>
            )}
          </Formik>
        </LoadingOverlay>
      </Content>
    </Container>
  );
}
